import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { recipientsFetch } from '@/lib/recipients/api';
import { ApiClientError } from '@/lib/api/client';
import { stagingKeys, recipientKeys, importKeys } from '@/hooks/api/query-keys';
import type { StagingListResponse, StagingRow } from '@/lib/types/recipients';

// ============ Whole-set helpers ============

/** The largest page GET /recipients/staging returns (ListStagingDto @Max(200)). */
const STAGING_PAGE_SIZE = 200;
/** The most ids one POST /recipients/staging/bulk-resolve may name (BulkResolveStagingDto @ArrayMaxSize(500)). */
const BULK_RESOLVE_MAX_IDS = 500;

/**
 * EVERY staging row matching the filters, all pages. For a screen that treats
 * its list as complete — a badge that counts it, an "All" that acts on it —
 * one page is a wrong number (item 2 and SI10, audit 2026-09-25).
 */
export async function fetchEveryStagingRow(filters: {
  status?: string;
  batchId?: string;
  sourceType?: string;
}): Promise<StagingRow[]> {
  const rows: StagingRow[] = [];
  for (let page = 1; page <= 1000; page++) {
    const params = new URLSearchParams();
    if (filters.status) params.set('status', filters.status);
    if (filters.batchId) params.set('batchId', filters.batchId);
    if (filters.sourceType) params.set('sourceType', filters.sourceType);
    params.set('page', String(page));
    params.set('limit', String(STAGING_PAGE_SIZE));
    const res = await recipientsFetch<StagingListResponse>(`staging?${params.toString()}`);
    const batch = res?.data ?? [];
    rows.push(...batch);
    if (batch.length < STAGING_PAGE_SIZE || rows.length >= (res?.total ?? 0)) break;
  }
  return rows;
}

/** What a bulk Approve / Reject did, in ROWS. */
export interface BulkResolveResult {
  /** Rows sent. */
  requested: number;
  /** Rows the API resolved (`count`, summed over the requests). */
  count: number;
  /** Rows the API could not resolve (`skippedCount`, summed). */
  skippedCount: number;
  /** Set when a request failed; the rows after it were not sent. */
  error?: string;
  /**
   * The failed request's errorCode (ApiClientError.errorCode), when the API
   * sent one — what a screen explains a refusal with (`error` is the API's
   * English, never shown).
   */
  errorCode?: string;
}

/**
 * Resolve every id, in requests the API accepts (at most 500 ids each),
 * summing the API's own `{ count, skippedCount }`. Stops at a failed request
 * and says how far it got. The caller invalidates the caches it shows.
 */
export async function bulkResolveInChunks(
  ids: readonly string[],
  action: 'Approve' | 'Reject',
): Promise<BulkResolveResult> {
  const result: BulkResolveResult = { requested: ids.length, count: 0, skippedCount: 0 };
  for (let i = 0; i < ids.length; i += BULK_RESOLVE_MAX_IDS) {
    try {
      const answer = await recipientsFetch<{ count: number; skippedCount: number }>(
        'staging/bulk-resolve',
        {
          method: 'POST',
          body: JSON.stringify({ ids: ids.slice(i, i + BULK_RESOLVE_MAX_IDS), action }),
        },
      );
      result.count += answer?.count ?? 0;
      result.skippedCount += answer?.skippedCount ?? 0;
    } catch (err) {
      result.error = err instanceof Error ? err.message : String(err);
      if (err instanceof ApiClientError && err.errorCode) result.errorCode = err.errorCode;
      break;
    }
  }
  return result;
}

// ============ Filter Types ============

export interface StagingFilters {
  status?: string;
  batchId?: string;
  sourceType?: string;
  page?: number;
  limit?: number;
  /**
   * When true, returns ONLY rows that still need user action — status IN
   * (New, Conflict, Invalid). Pairs with the "All" tab on the Pending
   * Actions page so the table's `total` matches the header badge
   * (closes the badge=2 vs table=233 production mismatch). Ignored when
   * `status` is also set — explicit > convenience.
   */
  actionable?: boolean;
  /**
   * #830 — only rows staged by this import (recipient_staging.source_id): a
   * CSV upload or an AD Syncer run, within the active workspace.
   */
  importId?: string;
}

// ============ Query Hooks ============

export function usePendingCount() {
  return useQuery({
    queryKey: stagingKeys.pendingCount(),
    queryFn: () => recipientsFetch<{ count: number }>('staging/pending-count'),
    refetchInterval: 30000,
  });
}

export function useStagingList(filters?: StagingFilters) {
  const params = new URLSearchParams();
  if (filters?.status) params.set('status', filters.status);
  if (filters?.batchId) params.set('batchId', filters.batchId);
  if (filters?.sourceType) params.set('sourceType', filters.sourceType);
  if (filters?.importId) params.set('importId', filters.importId);
  if (filters?.page != null) params.set('page', String(filters.page));
  if (filters?.limit != null) params.set('limit', String(filters.limit));
  // BE ignores `actionable` when `status` is also set — only one wins.
  if (filters?.actionable && !filters?.status) params.set('actionable', 'true');

  const queryString = params.toString();
  const path = queryString ? `staging?${queryString}` : 'staging';

  return useQuery({
    queryKey: stagingKeys.list(filters as Record<string, unknown> | undefined),
    queryFn: () => recipientsFetch<StagingListResponse>(path),
    enabled: !!filters,
  });
}

// ============ Mutation Hooks ============

// #830 — resolving a waiting row changes its import: the rows applied ("2 / 3"
// becomes "3 / 3"), how many still wait, and — on the last one — its status
// ('Needs Review' becomes 'Completed' or 'Partial'). The app's QueryClient keeps
// data fresh for five minutes, so every resolve path also invalidates the
// imports cache, or Import History and the Pending Actions import panel go on
// showing the import as waiting.

export function useResolveConflict() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      action,
      mergedData,
    }: {
      id: string;
      action: 'Approve' | 'Reject' | 'Merge';
      mergedData?: Record<string, unknown>;
    }) =>
      recipientsFetch<{ success: boolean }>(`staging/${id}/resolve`, {
        method: 'POST',
        body: JSON.stringify({ action, mergedData }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: stagingKeys.all() });
      queryClient.invalidateQueries({ queryKey: recipientKeys.all() });
      queryClient.invalidateQueries({ queryKey: importKeys.all() });
    },
    // #830 Part C (review fix) — no toast here. This one printed the API's
    // English message ("Staging row <uuid> is already resolved…") to an
    // Arabic, Hindi or French admin, and stacked on the caller's own. Every
    // caller says what failed itself, in the reader's language (the conflict
    // dialog maps the API's errorCode to a sentence).
  });
}

export function useBulkResolve() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      ids,
      action,
    }: {
      ids: string[];
      action: 'Approve' | 'Reject';
    }) =>
      // The API's actual answer (staging-resolver.service.ts `bulkResolve`):
      // rows resolved, and rows it could not resolve (counted, not named).
      // There is no `success` field — FE6 (audit 2026-09-25).
      recipientsFetch<{ count: number; skippedCount: number }>('staging/bulk-resolve', {
        method: 'POST',
        body: JSON.stringify({ ids, action }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: stagingKeys.all() });
      queryClient.invalidateQueries({ queryKey: recipientKeys.all() });
      queryClient.invalidateQueries({ queryKey: importKeys.all() });
    },
    // #830 Part C (review fix) — no toast here: Pending Actions' table says a
    // bulk action failed in the reader's language, and this one added the
    // API's English message beside it.
  });
}
